// Module-cached hook for admin-editable assessment text overrides.
// Covers: question title, question helper (subtitle), option labels,
//         phase section title, phase insight title/body.
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

let _cache = null;             // { map: {...}, phases: {...} }
let _promise = null;
const _listeners = new Set();

async function _load() {
  if (_cache) return _cache;
  if (_promise) return _promise;
  _promise = axios.get(`${API}/questions/overrides`)
    .then((r) => {
      _cache = {
        map: r.data?.map || {},
        phases: r.data?.phases || {},
        labels: r.data?.labels || {},
      };
      return _cache;
    })
    .catch(() => { _cache = { map: {}, phases: {}, labels: {} }; return _cache; })
    .finally(() => { _promise = null; });
  return _promise;
}

// Returns the full overrides object: { map, phases }.
// For backwards compatibility, callers that historically used the plain
// question map still work because we return a Proxy-like plain object with
// both shapes present; call sites can do `overrides.map[qId]` OR use the
// resolve helpers below.
export function useQuestionOverrides() {
  const [state, setState] = useState(_cache || { map: {}, phases: {}, labels: {} });
  useEffect(() => {
    let alive = true;
    _load().then((m) => { if (alive) setState(m); });
    const on = (m) => { if (alive) setState({ ...m }); };
    _listeners.add(on);
    return () => { alive = false; _listeners.delete(on); };
  }, []);
  return state;
}

export function refreshQuestionOverrides() {
  _cache = null;
  return _load().then((m) => { _listeners.forEach((cb) => cb(m)); return m; });
}

// ─── Resolvers ────────────────────────────────────────────────────────
// Every resolver falls back to the hardcoded default if no override exists.
// `overrides` can be the new shape ({map, phases}) or the legacy plain map.
function _mapOf(overrides) {
  if (!overrides) return {};
  if (overrides.map) return overrides.map;
  return overrides; // legacy: caller passed the map directly
}

export function resolveTitle(overrides, qId, lang, fallbackMap) {
  const t = _mapOf(overrides)?.[qId]?.title;
  if (t && (t[lang] || t.en)) return t[lang] || t.en;
  return fallbackMap?.[lang] || fallbackMap?.en || "";
}

export function resolveHelper(overrides, qId, lang, fallbackMap) {
  const h = _mapOf(overrides)?.[qId]?.helper;
  if (h && (h[lang] || h.en)) return h[lang] || h.en;
  return fallbackMap?.[lang] || fallbackMap?.en || "";
}

export function resolveBridge(overrides, qId, lang, fallbackMap) {
  const b = _mapOf(overrides)?.[qId]?.bridge;
  if (b && (b[lang] || b.en)) return b[lang] || b.en;
  return fallbackMap?.[lang] || fallbackMap?.en || "";
}

export function resolveLabel(overrides, qId, oId, lang, fallbackMap) {
  const l = _mapOf(overrides)?.[qId]?.options?.[oId]?.label;
  if (l && (l[lang] || l.en)) return l[lang] || l.en;
  return fallbackMap?.[lang] || fallbackMap?.en || "";
}

// Phase-level resolver. `field` is "title" | "insight_title" | "insight_body".
export function resolvePhase(overrides, phaseId, field, lang, fallbackMap) {
  const p = overrides?.phases?.[phaseId]?.[field];
  if (p && (p[lang] || p.en)) return p[lang] || p.en;
  return fallbackMap?.[lang] || fallbackMap?.en || "";
}

// Shared UI-label resolver. `key` is one of "continue" | "next" | "prev".
// Falls back to the provided default (usually a tr.* string from i18n).
export function resolveUiLabel(overrides, key, lang, fallback) {
  const l = overrides?.labels?.[key];
  if (l && typeof l[lang] === "string" && l[lang].trim()) return l[lang];
  if (l && typeof l.en === "string" && l.en.trim()) return l.en;
  return fallback || "";
}
